import { createFilterOptions } from '@mui/material'
import { Filter } from 'components/core/search-filter/filter'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { CancelFunc, emptyCancel } from 'lib/cancel'
import { UserContext } from 'lib/context/app'
import { SearchUsersRequest } from 'pbts/c1/api/user/v1/user'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { userSearchApi } from 'redux_toolkit/users'

const UserManagerFilterOptions = createFilterOptions<string>({})

type UserFilterManagerProps = {
    value?: Array<string>
    onChange: (updated: { managerIds: Array<string> }, shouldPropagate?: boolean) => void
}

export const UserManagerFilter = ({ value, onChange: _onChange }: UserFilterManagerProps): React.ReactElement => {
    const intl = useIntl()
    const userContext = useContext(UserContext)
    const [query, setQuery] = useState<string>('')
    const [selectedUsers, setSelectedUsers] = useState<Array<string>>(value || [])

    const searchRequest = useMemo(() => SearchUsersRequest.create({
        query,
        pageSize: 100,
        expandMask: { paths: ['*'] },
    }), [query])

    const selectedUsersSearchRequest = useMemo(() => SearchUsersRequest.create({
        ids: selectedUsers,
        pageSize: 100,
        expandMask: { paths: ['*'] },
    }), [selectedUsers])

    const { data: selectedUsersData } = userSearchApi.useSearch(selectedUsersSearchRequest)

    const { trigger: _trigger, data, isError, isLoading } = userSearchApi.useLazySearch()

    const trigger = useCallback(() => _trigger(searchRequest), [_trigger, searchRequest])

    const search = (q: string): CancelFunc => {
        setQuery(q || '')
        return emptyCancel
    }

    const flattened = useMemo(() => {
        return data ? data.flat() : []
    }, [data])

    const selectedUsersFlattened = useMemo(() => {
        return selectedUsersData?.flat() || []
    }, [selectedUsersData])

    const allFlattened = useMemo(() => {
        return [...selectedUsersFlattened, ...flattened]
    }, [selectedUsersFlattened, flattened])

    // Add current user at the top of the options
    const optionsWithCurrentUser = useMemo((): string[] => {
        const currentUserId = userContext?.user?.id || null

        // Build other user IDs safely and sort by display name for consistent UX
        const otherUsers = flattened?.filter((user) => user?.id) || []
        const sortedOtherUsers = otherUsers.sort((a, b) =>
            (a.displayName || '').localeCompare(b.displayName || ''))

        // Dedupe IDs using Set and remove current user if present
        const uniqueOtherUserIds = Array.from(new Set(
            sortedOtherUsers
                .map((user) => user.id)
                .filter((id) => id !== currentUserId),
        ))

        return currentUserId ? [currentUserId, ...uniqueOtherUserIds] : uniqueOtherUserIds
    }, [flattened, userContext?.user?.id])

    const renderLabel = useCallback(
        (id: string): string => {
            // Show current user's display name
            if (id === userContext?.user?.id) {
                return userContext.user.displayName || userContext.user.email || ''
            }
            return allFlattened?.find((r) => r.id === id)?.displayName || ''
        },
        [allFlattened, userContext?.user?.id, userContext?.user?.displayName, userContext?.user?.email],
    )

    const renderOption = useCallback(
        (props: React.HTMLAttributes<HTMLLIElement>, id: string): React.ReactElement => {
            // Find the user object for this ID
            const user = id === userContext?.user?.id
                ? userContext.user
                : allFlattened?.find((r) => r.id === id)

            return (
                <li>
                    {user ? (
                        <UserDisplayName
                            user={user}
                            showAvatar
                            avatarSize="sm"
                            includeDetails={false}
                        />
                    ) : (
                        <span>{id}</span>
                    )}
                </li>
            )
        },
        [allFlattened, userContext?.user],
    )

    const onChange = useCallback((managerIds: Array<string>) => _onChange({ managerIds }, true), [_onChange])

    const initialSelected = useRef(value)

    useEffect(() => {
        if (initialSelected.current) trigger()
    }, [trigger])

    const onOptionSelected = useCallback((event: React.SyntheticEvent, option: Array<string>) => {
        setSelectedUsers(option)
    }, [setSelectedUsers])

    return (
        <Filter
            label={intl.formatMessage({
                id: 'user_filter_manager_type',
                defaultMessage: 'Managed by',
            })}
            options={optionsWithCurrentUser}
            selected={value}
            onOptionSelected={onOptionSelected}
            onChange={onChange}
            filterOptions={UserManagerFilterOptions}
            renderLabel={renderLabel}
            renderOption={renderOption}
            widePopper
            error={isError}
            errorText={intl.formatMessage({
                id: 'user_filter_manager_error',
                defaultMessage: 'Error loading managers',
            })}
            loading={isLoading}
            onOpen={trigger}
            search={search}
            clearOnBlur
            clearOnClick
        />
    )
}
